import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { useSEO } from "@/lib/useSEO";
import { api } from "@/lib/api";
import { MapPin, Calendar, Users, ArrowRight } from "lucide-react";

function eventStatus(starts_at, ends_at) {
  if (!starts_at) return { label: "TBA", upcoming: true };
  const now = new Date();
  const start = new Date(starts_at);
  const end = ends_at ? new Date(ends_at) : new Date(start.getTime() + 2 * 60 * 60 * 1000);
  if (end < now) return { label: "Past Event", upcoming: false };
  if (start <= now && now <= end) return { label: "Happening Now", upcoming: true };
  return { label: "Upcoming", upcoming: true };
}

function formatWhen(iso) {
  if (!iso) return "Date & time TBA";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" }) +
      " · " + d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
}

export default function Meetups() {
  useSEO({
    title: "Community Meetups — Free & Paid Boxing Events | Better Through Boxing",
    description: "Free and paid boxing community meetups, park sessions, and group workouts hosted by Coach Chris Persaud and The Flow State Method LLC. Register online for upcoming public events.",
    path: "/meetups",
    keywords: ["boxing community meetup", "free boxing class park", "group boxing workout", "outdoor boxing event NJ", "Coach Chris boxing meetup", "Flow State Method events", "Better Through Boxing community"],
  });

  const [meetups, setMeetups] = useState([]);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/meetups");
        if (cancelled) return;
        setMeetups(data || []);
        setStatus((data || []).length > 0 ? "ready" : "empty");
      } catch {
        if (!cancelled) { setMeetups([]); setStatus("empty"); }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const upcoming = meetups.filter((m) => eventStatus(m.starts_at, m.ends_at).upcoming);
  const past = meetups.filter((m) => !eventStatus(m.starts_at, m.ends_at).upcoming);

  return (
    <div className="App relative">
      <SiteHeader />
      <main className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-24" data-testid="meetups-page">
        {/* SEO-rich hero — always rendered, no loading-gate */}
        <header className="mb-14 max-w-3xl">
          <div className="text-[#FF331F] text-xs uppercase tracking-[0.3em] font-bold mb-3">Community · The Flow State Method LLC</div>
          <h1 className="font-display font-bold uppercase text-5xl md:text-7xl tracking-tight leading-none mb-6">Community<br />Meetups.</h1>
          <p className="text-zinc-300 text-base md:text-lg leading-relaxed mb-4">
            Free and paid boxing events hosted in public spaces by Coach Chris Persaud — outdoor park sessions, beginner-friendly group workouts, fundamentals clinics, and community shake-outs. Open to anyone curious about the sport.
          </p>
          <p className="text-zinc-400 leading-relaxed">
            Spots are limited per event. Register through this page — you&apos;ll get an email confirmation and any updates if details change. Looking for private coaching instead? See <Link to="/services" className="text-[#FF331F] hover:underline">virtual lessons</Link> or <Link to="/in-person-training" className="text-[#FF331F] hover:underline">NJ in-person training</Link>.
          </p>
        </header>

        {/* About this program — additional indexable copy */}
        <section className="border border-[#27272A] bg-[#0F0F0F] p-6 md:p-8 mb-12 grid md:grid-cols-3 gap-6" data-testid="meetups-about-band">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold mb-2">Outdoor & Public</div>
            <p className="text-sm text-zinc-400 leading-relaxed">Most meetups take place in parks or community spaces. Bring water, athletic clothes, and an open mind. Hand wraps and gloves are always optional.</p>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold mb-2">Beginners Welcome</div>
            <p className="text-sm text-zinc-400 leading-relaxed">Every meetup is designed so a first-timer can keep up. We cover stance, the Core 4 punches, and footwork basics in the warm-up — then layer from there.</p>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold mb-2">Always Notified</div>
            <p className="text-sm text-zinc-400 leading-relaxed">If a location, time, or rain plan changes, every registrant gets an email. Reply directly to reach Coach Chris with questions.</p>
          </div>
        </section>

        {/* Loading skeleton */}
        {status === "loading" && (
          <div className="grid md:grid-cols-2 gap-8" data-testid="meetups-loading">
            {[0, 1].map((i) => (
              <div key={i} className="border border-[#27272A] bg-[#0F0F0F] overflow-hidden animate-pulse">
                <div className="aspect-[16/9] bg-[#1A1A1A]" />
                <div className="p-6 space-y-3">
                  <div className="h-3 w-24 bg-[#1A1A1A]" />
                  <div className="h-6 w-3/4 bg-[#1A1A1A]" />
                  <div className="h-4 w-full bg-[#1A1A1A]" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Upcoming */}
        {status === "ready" && upcoming.length > 0 && (
          <section className="mb-16" data-testid="meetups-upcoming">
            <h2 className="font-display uppercase text-2xl md:text-3xl mb-6">Upcoming</h2>
            <div className="grid md:grid-cols-2 gap-8">
              {upcoming.map((m) => <MeetupCard key={m.id} m={m} />)}
            </div>
          </section>
        )}

        {/* Past */}
        {status === "ready" && past.length > 0 && (
          <section data-testid="meetups-past">
            <h2 className="font-display uppercase text-2xl md:text-3xl mb-6 text-zinc-300">Past Events</h2>
            <div className="grid md:grid-cols-2 gap-8">
              {past.map((m) => <MeetupCard key={m.id} m={m} past />)}
            </div>
          </section>
        )}

        {/* Empty state — content-rich so Googlebot never sees an empty page */}
        {status === "empty" && (
          <section className="border border-[#27272A] bg-[#0F0F0F] p-10 md:p-14" data-testid="meetups-empty">
            <div className="text-[#FF331F] text-xs uppercase tracking-[0.3em] font-bold mb-3">Stay Tuned</div>
            <h2 className="font-display uppercase text-3xl md:text-4xl tracking-tight mb-4">New meetups coming soon.</h2>
            <p className="text-zinc-300 leading-relaxed mb-3">Coach Chris is putting the next round of community boxing meetups together right now. Free park sessions and small-group fundamentals clinics drop here first.</p>
            <p className="text-zinc-400 text-sm leading-relaxed mb-6">In the meantime, follow @flow_state_method on Instagram for first dibs when registration opens — or jump into a <Link to="/services" className="text-[#FF331F] hover:underline">live virtual session</Link> with Coach Chris this week.</p>
            <div className="flex flex-wrap gap-3">
              <Link to="/services" className="inline-flex items-center bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest text-xs px-5 py-3 rounded-sm">See services <ArrowRight className="ml-2 h-3 w-3" /></Link>
              <a href="https://www.instagram.com/flow_state_method/" target="_blank" rel="noreferrer" className="inline-flex items-center border border-zinc-700 text-white hover:bg-zinc-900 font-bold uppercase tracking-widest text-xs px-5 py-3 rounded-sm">Follow on Instagram</a>
            </div>
          </section>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}

function MeetupCard({ m, past = false }) {
  const free = !m.price_cents || m.price_cents === 0;
  const status = eventStatus(m.starts_at, m.ends_at);
  return (
    <Link
      to={`/meetups/${m.slug}`}
      className="group border border-[#27272A] bg-[#0F0F0F] overflow-hidden hover:border-[#FF331F]/50 transition-colors block"
      data-testid={`meetup-card-${m.slug}`}
    >
      <div className="relative aspect-[16/9] overflow-hidden bg-[#1A1A1A]">
        {m.cover_image && (
          <img src={m.cover_image} alt={m.title} className={`w-full h-full object-cover transition-transform duration-700 ${past ? "grayscale" : "group-hover:scale-105"}`} />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F0F0F]/80 via-transparent to-transparent" />
        <div className="absolute top-3 left-3 bg-[#FF331F] text-white text-[10px] uppercase tracking-widest font-bold px-2 py-1">{status.label}</div>
        <div className="absolute top-3 right-3 bg-[#050505]/80 backdrop-blur text-white text-[10px] uppercase tracking-widest font-bold px-2 py-1 border border-[#27272A]">{free ? "Free" : `$${(m.price_cents / 100).toFixed(0)}`}</div>
      </div>
      <div className="p-6">
        <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-3">
          <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {formatWhen(m.starts_at)}</span>
        </div>
        <h3 className="font-display uppercase text-2xl md:text-3xl leading-tight mb-2 group-hover:text-[#FF331F] transition-colors">{m.title}</h3>
        {m.subtitle && <p className="text-zinc-400 text-sm leading-relaxed mb-3">{m.subtitle}</p>}
        <div className="flex items-center gap-2 text-xs text-zinc-400">
          <MapPin className="h-3 w-3 text-[#FF331F]" /> {m.location_name || "Location TBA"}
        </div>
        {m.capacity ? (
          <div className="flex items-center gap-2 text-xs text-zinc-500 mt-2">
            <Users className="h-3 w-3" /> Cap: {m.capacity}
          </div>
        ) : null}
        <div className="mt-4 inline-flex items-center text-[#FF331F] text-xs uppercase tracking-widest font-bold">
          {past ? "View recap" : "Register"} <ArrowRight className="ml-1 h-3 w-3 group-hover:translate-x-1 transition-transform" />
        </div>
      </div>
    </Link>
  );
}
